import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { Report as ReportIcon } from '@mui/icons-material';
import { getClientsForCoach, createClientReport, getCoachByUid } from '../utils/firebaseUtils';
import { useAuth } from '../contexts/AuthContext';
import { isCoach } from '../utils/adminUtils';

const CoachClientsPage = () => {
  const { currentUser, approvedCoachUids } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportClient, setReportClient] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reporting, setReporting] = useState(false);

  const coach = currentUser ? getCoachByUid(currentUser.uid) : null;
  const coachId = coach?.id || 'elvis';

  const loadClients = async () => {
    if (!coachId) return;
    setLoading(true);
    setError('');
    try {
      const list = await getClientsForCoach(coachId);
      setClients(list);
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti klientų');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [coachId]);

  const openReportDialog = (client) => {
    setReportClient(client);
    setReportReason('');
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setReportDialogOpen(false);
    setReportClient(null);
    setReportReason('');
  };

  const handleReportConfirm = async () => {
    if (!reportClient || !currentUser) return;
    setReporting(true);
    setError('');
    try {
      await createClientReport({
        coachId,
        coachName: coach?.name || currentUser.displayName || 'Treneris',
        coachUid: currentUser.uid,
        clientName: reportClient.clientName,
        clientEmail: reportClient.clientEmail,
        clientPhone: reportClient.clientPhone,
        reason: reportReason.trim()
      });
      closeReportDialog();
    } catch (err) {
      console.error(err);
      setError('Nepavyko išsiųsti pranešimo');
    } finally {
      setReporting(false);
    }
  };

  if (!currentUser || !isCoach(currentUser, approvedCoachUids)) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik treneriai gali peržiūrėti šį puslapį.
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Klientai
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {clients.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Kol kas nėra klientų su rezervacijomis.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Vardas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>El. paštas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Telefonas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rezervacijų</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell sx={{ width: 48 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                        {(client.clientName || client.clientEmail || '?')[0].toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>{client.clientName || '—'}</TableCell>
                    <TableCell>
                      {client.clientEmail ? (
                        <Link href={`mailto:${client.clientEmail}`}>{client.clientEmail}</Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {client.clientPhone ? (
                        <Link href={`tel:${client.clientPhone}`}>{client.clientPhone}</Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{client.reservationCount || 0}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<ReportIcon />}
                        onClick={() => openReportDialog(client)}
                        sx={{ borderRadius: 2 }}
                      >
                        Raportuoti
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Report Dialog */}
        <Dialog
          open={reportDialogOpen}
          onClose={() => !reporting && closeReportDialog()}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle>Raportuoti klientą</DialogTitle>
          <DialogContent>
            {reportClient && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {reportClient.clientName || reportClient.clientEmail || '—'}
              </Typography>
            )}
            <TextField
              label="Priežastis (privaloma)"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              fullWidth
              multiline
              rows={4}
              size="small"
              placeholder="Paaiškinkite, kodėl norite pranešti apie šį klientą..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeReportDialog} disabled={reporting} sx={{ borderRadius: 2 }}>
              Atšaukti
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={reporting ? <CircularProgress size={20} /> : <ReportIcon />}
              onClick={handleReportConfirm}
              disabled={reporting || !reportReason.trim()}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {reporting ? 'Siunčiama...' : 'Patvirtinti'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CoachClientsPage;
